<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('borrowingByProject.pageTitle') }}</h1>
      <ts-breadcrumb
        :items="[
          { text: $t('home'), href: '/' },
          {
            text: $t('borrowingByProject.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-loading-banner :loading="loading">
      <ts-card>
        <ts-card-wrapper>
          <div class="row">
            <div class="col-md-3">
              <label>{{ $t('borrowingByProject.project') }}</label>
              <a-select
                v-model="model.project_id"
                :options="projects"
                showSearch
                :filterOption="filterOption"
                placeholder="--select--"
                mode="multiple"
                class="ts-w-full"
                :max-tag-count="1"
                :maxTagTextLength="30"
                @change="fetchBorrowing()"
              >
              </a-select>
              <ts-checkbox v-model="model.clearance" class="ts-mt-2">{{
                $t('borrowingByProject.clearance')
              }}</ts-checkbox>
              <div class="validate" v-if="errors.has('project_id')">
                {{ errors.first('project_id') }}
              </div>
            </div>
            <div class="col-md-3">
              <label>{{ $t('borrowingByProject.borrowingNumber') }}</label>
              <a-select
                v-model="model.sfo_id"
                :options="borrowing"
                showSearch
                placeholder="--select--"
                mode="multiple"
                class="ts-w-full"
                :max-tag-count="1"
                :maxTagTextLength="30"
                :filter-option="false"
                :not-found-content="loading ? undefined : null"
                @search="onSearchBorrowing"
              >
              </a-select>
            </div>
            <div class="col-md-3">
              <label>{{ $t('borrowingByProject.requestBy') }}</label>
              <a-select
                v-model="model.employee_id"
                :options="owners"
                showSearch
                :filterOption="filterOption"
                placeholder="--select--"
                mode="multiple"
                class="ts-w-full"
                :max-tag-count="1"
                :maxTagTextLength="30"
              >
              </a-select>
            </div>
          </div>
          <div class="row ts-mt-2">
            <div class="col-md-9 ts-flex ts-justify-end ts-space-x-2 ts-h-8">
              <ts-button @click="onPreview" color="primary">{{
                $t('preview')
              }}</ts-button>
              <ts-button @click="exportExcel" color="info">{{
                $t('excel')
              }}</ts-button>
            </div>
          </div>
        </ts-card-wrapper>
      </ts-card>
      <ts-card>
        <ts-card-wrapper>
          <ts-table :columns="columns" :records="resources">
            <template v-slot="{ record, index }">
              <td class="ts-whitespace-no-wrap">{{ index + 1 }}</td>
              <td class="ts-whitespace-no-wrap">{{ record.sfo_number }}</td>
              <td class="ts-whitespace-no-wrap">{{ record.service_date }}</td>
              <td class="ts-whitespace-no-wrap">{{ record.barcode }}</td>
              <td class="ts-whitespace-no-wrap">
                {{ record.product_name_en }}
              </td>
              <td class="ts-whitespace-no-wrap">
                {{ record.location_name_en }}
              </td>
              <td class="ts-whitespace-no-wrap ts-text-center">
                {{ record.quantity_order }}
              </td>
              <td class="ts-whitespace-no-wrap ts-text-center">
                {{ record.used_qty }}
              </td>
              <td class="ts-whitespace-no-wrap ts-text-center">
                {{ record.quantity_return }}
              </td>
              <td class="ts-whitespace-no-wrap ts-text-center">
                {{ record.lost_qty }}
              </td>
              <td class="ts-whitespace-no-wrap">
                {{ record.request_name }}
              </td>
              <td class="ts-whitespace-no-wrap">
                {{ record.delivery_name }}
              </td>
              <td class="ts-whitespace-no-wrap">
                {{ record.receive_name }}
              </td>
              <td class="ts-whitespace-no-wrap">
                {{ record.project_name }}
              </td>
              <td class="ts-whitespace-no-wrap">
                {{ record.customer_name }}
              </td>
              <td class="ts-whitespace-no-wrap">
                {{ record.borrowing_ref }}
              </td>
              <td class="ts-whitespace-no-wrap">
                {{ record.remarks }}
              </td>
            </template>
          </ts-table>
        </ts-card-wrapper>
      </ts-card>
    </ts-loading-banner>
  </div>
</template>

<script>
import { Errors } from 'form-backend-validation'
import { mapActions } from 'vuex'
import { debounce, trim } from 'lodash'
import * as FileDownload from 'downloadjs'
export default {
  name: 'borrowingByProject',
  data() {
    return {
      errors: new Errors(),
      exporting: false,
      loading: false,
      projects: [],
      resources: [],
      borrowing: [],
      owners: [],
      search: '',
      model: {
        project_id: undefined,
        sfo_id: undefined,
        employee_id: undefined,
        clearance: false
      }
    }
  },
  mounted() {
    this.fetchData()
  },
  computed: {
    columns() {
      return [
        { name: this.$t('borrowingByProject.numbering') },
        { name: this.$t('borrowingByProject.borrwingNumber') },
        { name: this.$t('borrowingByProject.borrowingDate') },
        { name: this.$t('borrowingByProject.barcode') },
        { name: this.$t('borrowingByProject.productName') },
        { name: this.$t('borrowingByProject.warehouseName') },
        { name: this.$t('borrowingByProject.borrowQty') },
        { name: this.$t('borrowingByProject.usedQty') },
        { name: this.$t('borrowingByProject.returnQty') },
        { name: this.$t('borrowingByProject.lostQty') },
        { name: this.$t('borrowingByProject.requestBy') },
        { name: this.$t('borrowingByProject.deliveryBy') },
        { name: this.$t('borrowingByProject.receivedBy') },
        { name: this.$t('borrowingByProject.projectName') },
        { name: this.$t('borrowingByProject.customerName') },
        { name: this.$t('borrowingByProject.borrowingRef') },
        { name: this.$t('borrowingByProject.remark') }
      ]
    }
  },
  methods: {
    ...mapActions('report/project', [
      'getOwner',
      'getProject',
      'getBorrowing',
      'borrowingByProject',
      'borrowingByProjectExcel'
    ]),
    async fetchData() {
      this.loading = true
      await this.getProject().then(response => {
        this.projects = response.map(el => ({
          value: el.project_id,
          label: el.project_name
        }))
      })
      await this.getOwner().then(response => {
        this.owners = response.map(el => ({
          value: el.employee_id,
          label: el.employee_name_en
        }))
      })
      await this.getBorrowing().then(response => {
        this.borrowing = response.map(el => ({
          value: el.sfo_id,
          label: el.sfo_number
        }))
      })
      this.loading = false
    },
    async fetchBorrowing(search = undefined) {
      this.model.sfo_id = undefined
      this.borrowing = []
      this.loading = true
      await this.getBorrowing({
        project_id: this.model.project_id,
        search
      }).then(response => {
        this.borrowing = response.map(el => ({
          value: el.sfo_id,
          label: el.sfo_number
        }))
      })
      this.loading = false
    },
    onSearchBorrowing: debounce(function(search) {
      this.fetchBorrowing(search)
    }, 500),
    onPreview() {
      this.loading = true
      this.borrowingByProject(this.model)
        .then(result => {
          this.resources = result.data
        })
        .catch(err => {
          this.$toasted.error(err.message)
          this.errors = new Errors(err.errors)
        })
        .finally(() => (this.loading = false))
    },
    exportExcel() {
      this.loading = true
      this.errors = new Errors()
      this.borrowingByProjectExcel(this.model)
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
          this.errors = new Errors(error.errors)
        })
        .finally(() => (this.loading = false))
    },
    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      )
    }
  }
}
</script>
